import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { CartItem } from "../types/common";

// Assumptions about CartItem:
// { packageId: string; price: number; quantity?: number; ... }
// prevent from adding more than total of 5 items
type CartState = {
  cartItems: CartItem[];
  loading: boolean;
  error: string | null;
  getCart: () => Promise<void>;
  addToCart: (items: CartItem[]) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  clearCart: () => Promise<void>;
  increaseQuantity: (id: string) => Promise<void>;
  decreaseQuantity: (id: string) => Promise<void>;
  getTotalQuantity: () => number;
  getTotalPrice: () => number;
  getQuantityById: (id: string) => number;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      cartItems: [],
      loading: false,
      error: null,

      // Local-only: no fetch; just ensure state is sane.
      getCart: async () => {
        set({ loading: false, error: null });
      },

      addToCart: async (items: CartItem[]) => {
        set((state) => {
          const next = [...state.cartItems];

          for (const incoming of items) {
            const idx = next.findIndex(
              (ci) => ci.packageId === incoming.packageId
            );
            const incQty = incoming.quantity ?? 1;

            if (idx >= 0) {
              const existing = next[idx];
              next[idx] = {
                ...existing,
                quantity: (existing.quantity ?? 1) + incQty,
                price: existing.price ?? incoming.price,
              };
            } else {
              next.push({
                ...incoming,
                quantity: incQty,
              });
            }
          }

          return { cartItems: next, error: null };
        });
      },

      removeFromCart: async (id: string) => {
        set((state) => ({
          cartItems: state.cartItems.filter((item) => item.packageId !== id),
          error: null,
        }));
      },

      clearCart: async () => {
        set({ cartItems: [], error: null });
      },

      increaseQuantity: async (id: string) => {
        set((state) => ({
          cartItems: state.cartItems.map((item) =>
            item.packageId === id
              ? { ...item, quantity: (item.quantity ?? 1) + 1 }
              : item
          ),
          error: null,
        }));
      },

      decreaseQuantity: async (id: string) => {
        set((state) => {
          const next = state.cartItems
            .map((item) =>
              item.packageId === id
                ? { ...item, quantity: Math.max((item.quantity ?? 1) - 1, 0) }
                : item
            )
            .filter((item) => (item.quantity ?? 0) > 0); // remove if 0
          return { cartItems: next, error: null };
        }); // <-- fixed: removed an extra ")"
      },

      getTotalQuantity: () =>
        get().cartItems.reduce((sum, item) => sum + (item.quantity ?? 1), 0),

      getTotalPrice: () =>
        get().cartItems.reduce(
          (sum, item) => sum + (item.price || 0) * (item.quantity ?? 1),
          0
        ),

      getQuantityById: (id: string) => {
        const item = get().cartItems.find((i) => i.packageId === id);
        return item?.quantity ?? 0;
      },
    }),
    {
      name: "cart", // key in localStorage
      storage: createJSONStorage(() => localStorage),
    }
  )
);
